import React, { useCallback, useEffect } from 'react';
import Document from '@uber/base-icons/document';
import VideoCamera from '@uber/base-icons/video-camera';
import { atom, useSetAtom } from 'jotai';
import { useDrawer } from '../../../../common/multi-level-drawer';
import type { ConfigT } from '../../../../common/types/types';
import { SidePanelProvider } from '../../../../modernization-common/side-panel';
import { TabsM3, TASK_QUESTIONNAIRE_ID, TASK_QUESTIONNAIRE_TITLE } from '../../../../modernization-common/side-panel/const';
import type { ModernizedGenericAnnotation } from '../../../../modernization-common/side-panel/types';
import { LABEL_TYPE_CONFIG, TOOL_CONFIG } from '../../../labelling/components/toolbar/constants';
import { annotationLabelsAtom, frameCountAtom } from '../../atoms/annotation-atoms';
import { taskQuestionnaireStatusAtom } from '../../atoms/m4-atoms';
import { annotationsAtom, filteredAnnotationsAtom, importedMLAnnotationIdsAtom, mlAnnotationsAtom, pendingAnnotationAtom, selectedFilterAtom } from '../../atoms/side-panel-atoms';
import { expandedAnnotationIdsAtom, expandedAnnotationSectionIdsAtom, visibilityModeAtom } from '../../atoms/side-panel-atoms';
import { focusedAnnotationIdAtom, selectedAnnotationIdsAtom, selectedAnnotationLabelAtom, showAnnotationDetailsAtom } from '../../atoms/tool-atoms';
import { LABELING_TITLES } from '../../constants';
import { AnnotationFilterContainer } from '../annotation-filter/annotation-filter';
import { AdditionalOptionsLabelWrapper } from '../side-panel/additional-options-wrapper';
import { DeleteAnnotationModalContainer } from '../side-panel/delete-annotation-modal-content/delete-annotation-modal-container';
import { useSidePanelHotkeys } from '../side-panel/hooks/use-side-panel-hotkeys';
import { useSidePanelQuestionnairePrefetch } from '../side-panel/hooks/use-side-panel-questionnaire-prefetch';
import { InstanceSegmentationButton } from '../side-panel/instance-segmentation-button';
import { createSidePanelDispatchAtom } from '../side-panel/side-panel-dispatch';
import { EditAnnotationsPanel } from './edit-annotation-panel/edit-annotation-panel';
import { RelationshipsTriggerButton } from './relationships-panel/relationships-trigger-button';
import { SidePanelContent } from './side-panel-content';
export type SidePanelImageAnnotation = ModernizedGenericAnnotation;

// Atom to track when edit button is clicked
export const editAnnotationTriggerAtom = atom<string | null>(null);
export const sidePanelDispatchAtom = createSidePanelDispatchAtom({
  onEditAnnotationStart: ({
    set,
    action
  }) => {
    set(editAnnotationTriggerAtom, action.annotationId);
  },
  onEditAnnotationEnd: ({
    set
  }) => {
    set(editAnnotationTriggerAtom, null);
  },
  mapSelectedAnnotationId: annotationId => annotationId || null
});
type PropsT = {
  classificationQuestionnaire?: {
    status: 'DONE' | 'PENDING' | 'OPTIONAL';
    Component: ({
      onClose
    }: {
      onClose?: () => void;
    }) => React.ReactNode;
  };
  labelingTitle?: string;
  onValidateAnswers?: (annotationId: string, answers: any) => Promise<any[]>;
  config?: ConfigT;
};
export const ImageSidePanel = ({
  classificationQuestionnaire,
  labelingTitle,
  onValidateAnswers,
  config
}: PropsT) => {
  const dispatch = useSetAtom(sidePanelDispatchAtom);
  const setTaskQuestionnaireStatus = useSetAtom(taskQuestionnaireStatusAtom);
  const {
    open,
    close
  } = useDrawer();
  useEffect(() => {
    setTaskQuestionnaireStatus(classificationQuestionnaire?.status);
  }, [classificationQuestionnaire?.status, setTaskQuestionnaireStatus]);
  const openTaskQuestionnaire = useCallback(() => {
    if (!classificationQuestionnaire) return;
    open({
      id: TASK_QUESTIONNAIRE_ID,
      title: TASK_QUESTIONNAIRE_TITLE,
      titleStartEnhancer: <Document size={16} />,
      content: ({
        onClose
      }) => classificationQuestionnaire.Component?.({
        onClose
      }),
      onClose: close,
      styleOverrides: {
        content: {
          padding: '0px'
        }
      }
    });
  }, [classificationQuestionnaire, open, close]);
  useSidePanelHotkeys({
    dispatch,
    onOpenTaskQuestionnaire: classificationQuestionnaire ? openTaskQuestionnaire : undefined
  });
  useSidePanelQuestionnairePrefetch();
  const additionalOptionsComponent = () => <>
      {labelingTitle === LABELING_TITLES.IMAGE && <AdditionalOptionsLabelWrapper />}
      <RelationshipsTriggerButton />
    </>;
  return <SidePanelProvider expandedAnnotationIdsAtom={expandedAnnotationIdsAtom} expandedAnnotationSectionIdsAtom={expandedAnnotationSectionIdsAtom} visibilityModeAtom={visibilityModeAtom} numAnnotationFramesAtom={frameCountAtom} annotationsAtom={annotationsAtom} mlAnnotationsAtom={mlAnnotationsAtom} importedMLAnnotationIdsAtom={importedMLAnnotationIdsAtom} filteredAnnotationsAtom={filteredAnnotationsAtom} annotationLabelsAtom={annotationLabelsAtom} dispatchAtom={sidePanelDispatchAtom} pendingAnnotationAtom={pendingAnnotationAtom} toolConfig={{
    ...TOOL_CONFIG,
    ...LABEL_TYPE_CONFIG,
    FRAME_CLASSIFICATION: {
      ...LABEL_TYPE_CONFIG.FRAME_CLASSIFICATION,
      Icon: ({
        strokeColor,
        size
      }: {
        strokeColor?: string;
        size?: number;
      }) => <VideoCamera color={strokeColor} size={size} />
    }
  }} selectedAnnotationLabelAtom={selectedAnnotationLabelAtom} selectedAnnotationIdsAtom={selectedAnnotationIdsAtom} selectedFilterLabelAtom={selectedFilterAtom} focusedAnnotationIdAtom={focusedAnnotationIdAtom} taskQuestionnaireComponent={classificationQuestionnaire} showAnnotationDetailsAtom={showAnnotationDetailsAtom} relationshipComponent={undefined} editAnnotationComponent={({
    onClose
  }) => <EditAnnotationsPanel dispatchAtom={sidePanelDispatchAtom} onClose={onClose} onValidateAnswers={onValidateAnswers} config={config} />} annotationFilterComponent={() => <AnnotationFilterContainer isM3SidePanelEnabled={true} />} labelListItemEnhancer={labelName => <InstanceSegmentationButton labelName={labelName} />} deleteAnnotationModalContentComponent={props => <DeleteAnnotationModalContainer {...props} />} additionalOptionsComponent={additionalOptionsComponent} defaultTab={TabsM3.CLASSES.key}>
      <SidePanelContent />
    </SidePanelProvider>;
};